import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { AuditChecklist } from '../components/docs/AuditChecklist';
import { BlockView, StateBadge, StatusPanel } from '../components/docs/Blocks';
import { DOC_PAGES, PAGES_BY_SLUG, navigation, searchDocs, validateDocs } from '../docs';

/**
 * The documentation surface.
 *
 * Public on purpose — someone evaluating the product, or a client trying to
 * understand an approval they have been sent, should not have to sign in to
 * read how it works.
 */

const DEFAULT_SLUG = 'overview';

export function DocsPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const current = PAGES_BY_SLUG[slug ?? DEFAULT_SLUG];
  // Development only: a docs set that can lie about its own completeness is
  // worse than none, so violations are shown rather than logged.
  const problems = useMemo(() => (import.meta.env.DEV ? validateDocs() : []), []);
  const sections = useMemo(navigation, []);
  const hits = useMemo(() => searchDocs(query), [query]);

  // Scroll back to the top on navigation. Without this, moving from the bottom
  // of a long page to a short one leaves the reader below its content.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setNavOpen(false);
  }, [slug]);

  // "/" focuses search, the convention every docs site shares — but not while
  // the reader is already typing somewhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === 'Escape') {
        setQuery('');
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openHit = (hitSlug: string) => {
    setQuery('');
    navigate(`/docs/${hitSlug}`);
  };

  return (
    <div className="min-h-screen bg-panel text-neutral-200">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-line bg-panel/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
          <Link to="/" className="shrink-0 text-sm font-semibold text-neutral-100">
            Chairly
          </Link>
          <span className="shrink-0 text-sm text-neutral-500">Docs</span>

          <div className="relative ml-auto w-full max-w-sm">
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the docs…  /"
              aria-label="Search documentation"
              className="w-full rounded-lg border border-line bg-panel-raised px-3 py-1.5 text-sm outline-none focus:border-accent"
            />

            {query.trim().length >= 2 && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-line bg-panel-raised shadow-xl">
                {hits.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-neutral-500">
                    Nothing matches “{query}”.
                  </p>
                ) : (
                  hits.map((hit) => (
                    <button
                      key={hit.page.slug}
                      onClick={() => openHit(hit.page.slug)}
                      className="block w-full border-b border-line px-3 py-2 text-left last:border-b-0 hover:bg-panel"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm text-neutral-100">{hit.page.title}</span>
                        {hit.page.status && <StateBadge state={hit.page.status.state} />}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-neutral-500">
                        {hit.excerpt ?? hit.page.summary}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setNavOpen((open) => !open)}
            className="shrink-0 rounded-lg border border-line px-2 py-1.5 text-xs text-neutral-400 md:hidden"
            aria-expanded={navOpen}
          >
            Contents
          </button>
        </div>
      </header>

      {problems.length > 0 && (
        <div className="border-b border-red-400/30 bg-red-500/10 px-4 py-2">
          <p className="mx-auto max-w-6xl text-xs text-red-200">
            <strong>Docs integrity:</strong> {problems.join(' · ')}
          </p>
        </div>
      )}

      <div className="mx-auto flex max-w-6xl gap-8 px-4 py-8">
        {/* Sidebar */}
        <nav
          className={`${navOpen ? 'block' : 'hidden'} w-full shrink-0 md:block md:w-56`}
          aria-label="Documentation"
        >
          <div className="md:sticky md:top-20 space-y-5">
            {sections.map((section) => (
              <div key={section.id}>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-600">
                  {section.title}
                </p>
                <ul className="space-y-0.5">
                  {section.pages.map((page) => {
                    const active = page.slug === current?.slug;
                    return (
                      <li key={page.slug}>
                        <Link
                          to={`/docs/${page.slug}`}
                          aria-current={active ? 'page' : undefined}
                          className={`flex items-center gap-2 rounded-md px-2 py-1 text-sm ${
                            active
                              ? 'bg-accent-soft text-accent'
                              : 'text-neutral-400 hover:text-neutral-100'
                          }`}
                        >
                          <span className="truncate">{page.title}</span>
                          {/* A dot rather than a full badge: the sidebar should
                              hint at unfinished work without shouting over the
                              page titles. */}
                          {page.status && page.status.state !== 'shipped' && (
                            <span
                              aria-label={page.status.state}
                              title={page.status.state}
                              className={`ml-auto h-1.5 w-1.5 shrink-0 rounded-full ${
                                page.status.state === 'missing' ? 'bg-red-400' : 'bg-amber-400'
                              }`}
                            />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* Content */}
        <main className={`${navOpen ? 'hidden' : 'block'} min-w-0 flex-1 md:block`}>
          {!current ? (
            <NotFound />
          ) : (
            <article className="max-w-3xl space-y-4">
              <header className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-semibold text-neutral-100">{current.title}</h1>
                  {current.status && <StateBadge state={current.status.state} />}
                </div>
                <p className="text-neutral-400">{current.summary}</p>
              </header>

              {current.status && <StatusPanel status={current.status} />}

              <div className="space-y-4">
                {current.blocks.map((block, i) => (
                  <BlockView key={i} block={block} />
                ))}
              </div>

              {/* The checklist page renders its table from the registry rather
                  than from blocks, so it can never fall out of step. */}
              {current.slug === 'audit-checklist' && (
                <div className="pt-2">
                  <AuditChecklist />
                </div>
              )}

              <PageFooter slug={current.slug} />
            </article>
          )}
        </main>
      </div>
    </div>
  );
}

function PageFooter({ slug }: { slug: string }) {
  const index = DOC_PAGES.findIndex((page) => page.slug === slug);
  const previous = index > 0 ? DOC_PAGES[index - 1] : undefined;
  const next = index >= 0 && index < DOC_PAGES.length - 1 ? DOC_PAGES[index + 1] : undefined;

  return (
    <nav className="flex flex-wrap justify-between gap-3 border-t border-line pt-5">
      {previous ? (
        <Link to={`/docs/${previous.slug}`} className="text-sm text-neutral-400 hover:text-accent">
          ← {previous.title}
        </Link>
      ) : (
        <span />
      )}
      {next && (
        <Link to={`/docs/${next.slug}`} className="text-sm text-neutral-400 hover:text-accent">
          {next.title} →
        </Link>
      )}
    </nav>
  );
}

function NotFound() {
  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold text-neutral-100">Page not found</h1>
      <p className="mt-2 text-neutral-400">
        That documentation page does not exist. Try the search, or start from the{' '}
        <Link to="/docs/overview" className="text-accent hover:underline">
          overview
        </Link>
        .
      </p>
    </div>
  );
}
